import { useRef } from 'react'

const REST_TRANSFORM = 'rotateY(-14deg) rotateX(7deg)'

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

const OLQ_SCORES = [
  { label: 'Planning & Organisation', pct: 92 },
  { label: 'Social Adjustment', pct: 90 },
  { label: 'Social Effectiveness', pct: 88 },
  { label: 'Dynamic', pct: 90 },
]

const BREAKDOWN = [
  { label: 'SCREENING', pct: 90 },
  { label: 'PSYCH TESTS', pct: 88 },
  { label: 'GTO TASKS', pct: 84 },
  { label: 'INTERVIEW', pct: 82 },
]

const OVERALL_PCT = 90

function CornerBracket({ className }: { className: string }) {
  return <div className={`absolute w-6 h-6 border-force-green-light/60 ${className}`} style={{ transform: 'translateZ(26px)' }} />
}

export default function ForceHeroCard() {
  const wrapRef = useRef<HTMLDivElement>(null)
  const tiltRef = useRef<HTMLDivElement>(null)
  const glareRef = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = wrapRef.current
    const tilt = tiltRef.current
    const glare = glareRef.current
    if (!el || !tilt || !glare) return
    const rect = el.getBoundingClientRect()
    const px = (e.clientX - rect.left) / rect.width
    const py = (e.clientY - rect.top) / rect.height
    tilt.style.transition = 'transform 60ms linear'
    tilt.style.transform = `rotateY(${lerp(-26, -2, px)}deg) rotateX(${lerp(16, -4, py)}deg)`
    glare.style.opacity = '1'
    glare.style.background = `radial-gradient(circle at ${px * 100}% ${py * 100}%, rgba(200,219,160,0.22), transparent 55%)`
  }

  const handleMouseLeave = () => {
    const tilt = tiltRef.current
    const glare = glareRef.current
    if (!tilt || !glare) return
    tilt.style.transition = 'transform 600ms cubic-bezier(0.22,1,0.36,1)'
    tilt.style.transform = REST_TRANSFORM
    glare.style.opacity = '0'
  }

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: '1400px' }}
      className="w-full"
    >
      <div
        ref={tiltRef}
        style={{ transform: REST_TRANSFORM, transformStyle: 'preserve-3d', transition: 'transform 600ms cubic-bezier(0.22,1,0.36,1)' }}
        className="relative w-full rounded-2xl border border-force-green/25 bg-[#0c0e09] shadow-[0_50px_100px_rgba(0,0,0,0.7),0_0_70px_rgba(92,122,63,0.18)]"
      >
        {/* recessed grid-paper backdrop, sunk behind the surface for real depth */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            transform: 'translateZ(-36px)',
            backgroundImage:
              'linear-gradient(rgba(143,175,95,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(143,175,95,0.12) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        {/* HUD corner brackets, floating above the surface */}
        <CornerBracket className="top-3 left-3 border-t-2 border-l-2 rounded-tl-sm" />
        <CornerBracket className="top-3 right-3 border-t-2 border-r-2 rounded-tr-sm" />
        <CornerBracket className="bottom-3 left-3 border-b-2 border-l-2 rounded-bl-sm" />
        <CornerBracket className="bottom-3 right-3 border-b-2 border-r-2 rounded-br-sm" />

        {/* live status chip, floating highest */}
        <div
          style={{ transform: 'translateZ(30px)' }}
          className="absolute -top-3 left-8 flex items-center gap-1.5 bg-[#0c0e09] border border-force-green/40 rounded-full px-2.5 py-1"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-force-green-light animate-pulse" />
          <span className="font-mono text-[10px] tracking-[0.15em] text-force-green-light">LIVE OLQ ANALYSIS</span>
        </div>

        {/* cursor-tracking glare, sells the sense of a physical tilted surface catching light */}
        <div
          ref={glareRef}
          style={{ transform: 'translateZ(32px)', opacity: 0, transition: 'opacity 300ms ease' }}
          className="absolute inset-0 rounded-2xl pointer-events-none"
        />

        <div
          style={{ transform: 'translateZ(4px)' }}
          className="relative rounded-2xl overflow-hidden p-5 md:p-6 pt-7 grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-5"
        >
          <div className="flex flex-col gap-2">
            <p className="font-mono text-[10px] tracking-[0.15em] text-force-text-muted/70 uppercase">OLQ Breakdown // 4 factors</p>
            <div className="relative h-48 md:h-52 flex flex-col justify-center gap-2.5">
              {OLQ_SCORES.map(({ label, pct }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="w-28 shrink-0 font-mono text-[9px] tracking-[0.1em] text-force-text-muted uppercase">
                    {label}
                  </span>
                  <div className="relative flex-1 h-1.5 rounded-full bg-force-green/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-force-green-light"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-7 shrink-0 text-right font-mono text-[9px] text-force-text">{pct}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 font-mono">
            <div>
              <p className="text-[10px] tracking-[0.15em] text-force-text-muted/70 uppercase">Overall Score</p>
              <p className="text-3xl font-bold text-force-green-light leading-tight">
                {OVERALL_PCT}
                <span className="text-sm text-force-text-muted">%</span>
              </p>
            </div>

            <div className="h-px bg-force-green/15" />

            <div>
              <p className="text-[10px] tracking-[0.15em] text-force-text-muted/70 uppercase mb-1.5">Assessment Breakdown</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {BREAKDOWN.map((b) => (
                  <div key={b.label} className="flex justify-between gap-2 text-[10px] border-b border-force-green/10 pb-1">
                    <span className="text-force-text-muted">{b.label}</span>
                    <span className="font-bold text-force-text">{b.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="h-px bg-force-green/15" />

            <div>
              <p className="text-[10px] tracking-[0.15em] text-force-text-muted/70 uppercase mb-1">AI Recommendation</p>
              <p className="text-[11px] text-force-text-muted leading-relaxed font-sans">
                Focus next on Group Discipline and Situation Reaction Test consistency.
              </p>
            </div>

            <button className="mt-1 self-start bg-force-green text-force-text font-sans text-[11px] font-semibold px-3 py-1.5 rounded-md hover:bg-force-green-light transition-colors">
              View Detailed Report →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
